import { EntityType } from "@mapmycustomers/shared/enum";
import Route, { RouteStopBulkItem } from "@mapmycustomers/shared/types/entity/Route";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { EditorRouteStop } from "../../types/RouteStop";

type Variant = EntityType.COMPANY | EntityType.PERSON;

interface EntityRef {
  entityId: number;
  entityType: Variant;
}

interface OriginalStopSnapshot {
  allottedTime: number;
  checkedOff: boolean;
  displayOrder: number;
  id: NonNullable<EditorRouteStop["id"]>;
  manualAddress?: string;
  manualGeoPoint?: GeoPoint;
  manualName?: string;
  scheduledTime?: null | string;
}

const stopKey = (entityType: Variant, entityId: number): string => `${entityType}:${entityId}`;

// Compare geoPoints to 6 decimals (~0.11 m). Finer differences are geocoder/GPS noise
// and must not flag a manual stop as changed.
const round6 = (value: number): number => Math.round(value * 1e6) / 1e6;
const sameGeoPoint = (left?: GeoPoint, right?: GeoPoint): boolean => {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return (
    round6(left.coordinates[0]) === round6(right.coordinates[0]) &&
    round6(left.coordinates[1]) === round6(right.coordinates[1])
  );
};

const getEntityRef = (stop: EditorRouteStop): EntityRef | undefined => {
  if (stop.entity === EntityType.COMPANY && isDefined(stop.accountId)) {
    return { entityId: stop.accountId, entityType: EntityType.COMPANY };
  }
  if (stop.entity === EntityType.PERSON && isDefined(stop.contactId)) {
    return { entityId: stop.contactId, entityType: EntityType.PERSON };
  }
  return undefined;
};

// Entity stops keyed by `${entityType}:${entityId}`; manual stops by pivot id.
const getStopKey = (stop: EditorRouteStop): string | undefined => {
  const ref = getEntityRef(stop);
  if (ref) {
    return stopKey(ref.entityType, ref.entityId);
  }
  if (stop.entity === null && isDefined(stop.id)) {
    return `manual:${stop.id}`;
  }
  return undefined;
};

const buildOriginalSnapshotMap = (originalRoute: Route): Map<string, OriginalStopSnapshot> => {
  const snapshot = new Map<string, OriginalStopSnapshot>();
  (originalRoute.stopTime ?? []).forEach((stop) => {
    const key = getStopKey(stop as EditorRouteStop);
    if (!key || !isDefined(stop.id)) {
      return;
    }
    snapshot.set(key, {
      id: stop.id,
      allottedTime: stop.allottedTime,
      checkedOff: stop.checkedOff,
      displayOrder: stop.displayOrder,
      scheduledTime: stop.scheduledTime ?? null,
      ...(stop.entity === null
        ? {
            manualAddress: stop.manualAddress,
            manualGeoPoint: stop.manualGeoPoint,
            manualName: stop.manualName,
          }
        : {}),
    });
  });
  return snapshot;
};

const buildStopsDiff = (
  originalRoute: Route,
  stops: ReadonlyArray<EditorRouteStop>
): RouteStopBulkItem[] => {
  const originalByKey = buildOriginalSnapshotMap(originalRoute);
  const currentKeys = new Set<string>();
  const operations: RouteStopBulkItem[] = [];

  stops.forEach((stop) => {
    const key = getStopKey(stop);
    if (key) {
      currentKeys.add(key);
    }
  });

  originalByKey.forEach((snapshot, key) => {
    if (!currentKeys.has(key)) {
      operations.push({ id: snapshot.id, _action_: "d" });
    }
  });

  stops.forEach((stop) => {
    const key = getStopKey(stop);
    if (!key || originalByKey.has(key)) {
      return;
    }
    const ref = getEntityRef(stop);
    if (ref) {
      operations.push(
        ref.entityType === EntityType.COMPANY
          ? {
              _action_: "c",
              accountId: ref.entityId,
              allottedTime: stop.allottedTime,
              displayOrder: stop.displayOrder,
              scheduledTime: stop.scheduledTime ?? null,
            }
          : {
              _action_: "c",
              allottedTime: stop.allottedTime,
              contactId: ref.entityId,
              displayOrder: stop.displayOrder,
              scheduledTime: stop.scheduledTime ?? null,
            }
      );
    } else if (stop.entity === null) {
      operations.push({
        _action_: "c",
        allottedTime: stop.allottedTime,
        displayOrder: stop.displayOrder,
        manualAddress: stop.manualAddress,
        manualGeoPoint: stop.manualGeoPoint,
        manualName: stop.manualName,
        scheduledTime: stop.scheduledTime ?? null,
      });
    }
  });

  stops.forEach((stop) => {
    const key = getStopKey(stop);
    if (!key) {
      return;
    }
    const prior = originalByKey.get(key);
    if (!prior) {
      return;
    }
    const baseChanged =
      prior.allottedTime !== stop.allottedTime ||
      prior.checkedOff !== stop.checkedOff ||
      prior.displayOrder !== stop.displayOrder ||
      (prior.scheduledTime ?? null) !== (stop.scheduledTime ?? null);
    const manualChanged =
      stop.entity === null &&
      (prior.manualName !== stop.manualName ||
        prior.manualAddress !== stop.manualAddress ||
        !sameGeoPoint(prior.manualGeoPoint, stop.manualGeoPoint));
    if (!baseChanged && !manualChanged) {
      return;
    }
    operations.push({
      id: prior.id,
      _action_: "u",
      allottedTime: stop.allottedTime,
      checkedOff: stop.checkedOff,
      displayOrder: stop.displayOrder,
      scheduledTime: stop.scheduledTime ?? null,
      ...(stop.entity === null
        ? {
            manualAddress: stop.manualAddress,
            manualGeoPoint: stop.manualGeoPoint,
            manualName: stop.manualName,
          }
        : {}),
    });
  });

  return operations;
};

export default buildStopsDiff;
